import React, { useRef, useState } from 'react';
import { Mail, MapPin, Building2 } from 'lucide-react';
import { FaGithub, FaLinkedinIn, FaXTwitter } from 'react-icons/fa6';
import { Container, Eyebrow, PillButton } from '../../../components/home/ui';
import Faq from '../../../components/home/Faq';

const details = [
  { label: 'Email', value: 'binudp.dev@gmail.com', href: 'mailto:binudp.dev@gmail.com', icon: Mail },
  { label: 'Location', value: 'Guwahati, Assam, India', icon: MapPin },
  { label: 'Company', value: 'Binud Software Solutions', href: 'https://binudsoftwaresolutions.in', icon: Building2 },
];

const socials = [
  { label: 'GitHub', href: 'https://github.com/devBinud', icon: <FaGithub className="w-4 h-4" /> },
  { label: 'LinkedIn', href: 'https://linkedin.com/in/binudpanging', icon: <FaLinkedinIn className="w-4 h-4" /> },
  { label: 'Twitter', href: 'https://x.com/binudp_dev', icon: <FaXTwitter className="w-4 h-4" /> },
];

const fields = [
  { id: 'name', label: 'Your Name', type: 'text', placeholder: 'John Doe' },
  { id: 'email', label: 'Your Email', type: 'email', placeholder: 'you@example.com' },
];

const inputCls =
  'w-full bg-[#e6e9f0] rounded-xl px-4 py-3.5 text-sm text-[#111] placeholder-[#111]/35 outline-none ring-1 ring-transparent focus:ring-[#3b5bfd] focus:bg-white transition';

const Contact = () => {
  const form = useRef();
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const sendMessage = async (e) => {
    e.preventDefault();
    setStatus('sending');
    const data = new FormData(form.current);
    try {
      // Loaded on submit so Firebase isn't in the main bundle.
      const [{ addDoc, collection, serverTimestamp }, { db }] = await Promise.all([
        import('firebase/firestore'),
        import('../../../config/firebase'),
      ]);
      await addDoc(collection(db, 'contacts'), {
        name: data.get('name').trim(),
        email: data.get('email').trim(),
        message: data.get('message').trim(),
        createdAt: serverTimestamp(),
      });
      setStatus('sent');
      form.current.reset();
    } catch (err) {
      setStatus('error');
      console.error(err);
    }
  };

  return (
    <>
      <section className="bg-linear-to-b from-[#d5d9e3] to-[#e9ecf2] pt-28 lg:pt-32 pb-20 lg:pb-28">
        <Container>
          <div className="max-w-[720px]">
            <Eyebrow>Contact</Eyebrow>
            <h1 className="mt-4 text-[40px] sm:text-[54px] lg:text-[62px] font-medium leading-[1.02] tracking-[-0.045em] text-[#111]">
              Let's build something
              <br />
              great together
            </h1>
            <p className="mt-5 max-w-[520px] text-[15px] sm:text-base leading-relaxed text-[#111]/70">
              Have a project in mind, an opportunity to discuss, or want to explore collaboration with Binud Software
              Solutions? Drop a message below.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Info card */}
            <div className="lg:col-span-5 rounded-3xl p-6 sm:p-8 flex flex-col bg-[radial-gradient(110%_90%_at_30%_10%,#3b5bfd_0%,#6f86ff_45%,#c9d2ff_80%,#eef1fb_100%)] shadow-[0_20px_50px_rgba(59,91,253,0.15)]">
              <Eyebrow dark starClassName="text-white">Get In Touch</Eyebrow>
              <h2 className="mt-3 text-[28px] sm:text-[32px] font-medium leading-[1.1] tracking-[-0.04em] text-white">
                Tell me about
                <br />
                your project
              </h2>

              <div className="mt-8 space-y-2.5">
                {details.map(({ label, value, href, icon: Icon }) => {
                  const body = (
                    <>
                      <span className="w-10 h-10 shrink-0 rounded-full bg-[#111] text-white flex items-center justify-center">
                        <Icon className="w-4 h-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-xs text-[#111]/55">{label}</span>
                        <span className="block text-[15px] text-[#111] truncate">{value}</span>
                      </span>
                    </>
                  );
                  const cls = 'flex items-center gap-4 rounded-2xl bg-white/85 backdrop-blur px-4 py-3';
                  return href ? (
                    <a key={label} href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className={`${cls} transition-transform hover:-translate-y-0.5`}>
                      {body}
                    </a>
                  ) : (
                    <div key={label} className={cls}>
                      {body}
                    </div>
                  );
                })}
              </div>

              <div className="mt-auto pt-8 flex items-center gap-2.5">
                {socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="w-10 h-10 rounded-full bg-[#111] text-white flex items-center justify-center transition-transform hover:-translate-y-0.5"
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Form */}
            <form ref={form} onSubmit={sendMessage} className="lg:col-span-7 rounded-3xl bg-[#f4f6fa] p-6 sm:p-8 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {fields.map((f) => (
                  <div key={f.id}>
                    <label htmlFor={f.id} className="block text-sm text-[#111]/75 mb-2">
                      {f.label}
                    </label>
                    <input id={f.id} name={f.id} type={f.type} placeholder={f.placeholder} required className={inputCls} />
                  </div>
                ))}
              </div>
              <div>
                <label htmlFor="message" className="block text-sm text-[#111]/75 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="7"
                  placeholder="Tell me about your project, timeline, and goals…"
                  required
                  className={`${inputCls} resize-none`}
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-1">
                <PillButton type="submit" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Sending…' : 'Send Message'}
                </PillButton>
                {status === 'sent' && <p className="text-sm text-[#16a34a]">Message sent - I'll get back to you soon.</p>}
                {status === 'error' && (
                  <p className="text-sm text-[#dc2626]">Something went wrong. Please try again or email me directly.</p>
                )}
              </div>
            </form>
            
          </div>
        </Container>
      </section>

      <Faq />
    </>
  );
};

export default Contact;
